import { generatePath } from 'react-router'

import type { LandingAction, LandingTarget } from '@entities/LandingPage'

import {
  URL_BLOG,
  URL_BLOG_POST,
  URL_CATEGORY_NEW_PRODUCTS,
  URL_CATEGORY_POPULAR_PRODUCTS,
  URL_CATEGORY_SALE_PRODUCTS,
  URL_PAGE_CATALOG,
  URL_PAGE_CATALOG_1ST_LVL,
  URL_PAGE_PRODUCT_GROUP,
} from '@shared/contants'

/*
  Where a landing target points. `external` links leave the site (new tab),
  everything else is an in-app route rendered as a router <Link>.
*/
export interface ResolvedLandingTarget {
  href: string;
  external: boolean;
}

/*
  Local pages a `kind: screen` target may address. The set is intentionally
  small — an unknown screen resolves to nothing rather than to a wrong page.
*/
const SCREEN_ROUTES: Record<string, string> = {
  catalog: URL_PAGE_CATALOG,
  popular: URL_CATEGORY_POPULAR_PRODUCTS,
  new: URL_CATEGORY_NEW_PRODUCTS,
  sale: URL_CATEGORY_SALE_PRODUCTS,
  blog: URL_BLOG,
  media: URL_BLOG,
}

const asString = (value: unknown): string | undefined => (
  value === null || value === undefined || value === '' ? undefined : String(value)
)

/*
  A text material is addressed by slug on the web (/media/category/:cat/:slug).
  When the target only carries an id we cannot build that path without an extra
  round-trip, so we fall back to the blog index instead of guessing.
*/
function resolveTextMaterial (target: LandingTarget): ResolvedLandingTarget | null {
  const slug = asString(target.textMaterialSlug)

  if (slug) {
    const categorySlug = asString(target.params?.categorySlug) ?? 'article'
    return {
      href: generatePath(URL_BLOG_POST, { categorySlug, slug }),
      external: false,
    }
  }

  return asString(target.textMaterialId) ? { href: URL_BLOG, external: false } : null
}

function resolveScreen (target: LandingTarget): ResolvedLandingTarget | null {
  const screen = asString(target.screen)

  if (!screen) {
    return null
  }

  // A category screen carries its slug in params (ids are not routable here).
  if (screen === 'category') {
    const categorySlug = asString(target.params?.categorySlug)
    return categorySlug
      ? { href: generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug }), external: false }
      : null
  }

  if (screen === 'product_group' || screen === 'productGroup') {
    const productGroupId = asString(target.params?.productGroupId)
    return productGroupId
      ? { href: generatePath(URL_PAGE_PRODUCT_GROUP, { productGroupId }), external: false }
      : null
  }

  const route = SCREEN_ROUTES[screen]
  return route ? { href: route, external: false } : null
}

/*
  Turns a landing target into something renderable. Returns null when the target
  is absent, inert (kind: none) or not addressable on the web — callers then
  render plain, non-clickable content instead of a dead link.

  Contest targets belong to promo landings and are deliberately not resolved
  here: this site has no contest pages, so they degrade to nothing.
*/
export function resolveLandingTarget (
  target?: LandingTarget | null,
): ResolvedLandingTarget | null {
  if (!target || target.kind === 'none') {
    return null
  }

  switch (target.kind) {
  case 'external': {
    const url = asString(target.url)
    return url ? { href: url, external: true } : null
  }

  case 'text_material':
    return resolveTextMaterial(target)

  case 'screen':
    return resolveScreen(target)

  default:
    // No kind, but a usable payload — be tolerant of hand-authored targets.
    if (asString(target.url)) {
      return { href: target.url as string, external: true }
    }
    return target.screen ? resolveScreen(target) : null
  }
}

/*
  Blocks may reference a shared action by id (payload.actionId) instead of
  carrying their own target. Resolves that indirection, falling back to the
  block's own target.
*/
export function resolveLandingAction (
  actions: LandingAction[] | null | undefined,
  actionId?: string | null,
  fallbackTarget?: LandingTarget | null,
): ResolvedLandingTarget | null {
  if (actionId) {
    const action = actions?.find((item) => item?.id === actionId)

    if (action) {
      return resolveLandingTarget(action.target)
    }
  }

  return resolveLandingTarget(fallbackTarget)
}
